// ═══════════════════════════════════════════════════════════════
// 📘 MÓDULO 9 - UNIDAD 3: MAKING DEDUCTIONS (Might & Could)
// ═══════════════════════════════════════════════════════════════
// ✅ Biblia v4.1 - Context a nivel SET, campo "questions"
// ✅ Progresión: A2+ → B2+ (10 sets)
// ✅ Narrativa: Emergency Room - Medical Deductions
// ═══════════════════════════════════════════════════════════════

export const M9_UNIT_3 = {
  id: "m9-u3-deductions",
  title: "Unidad 3: Making Deductions",
  topic: "Use might, could, might not, couldn't, and can't to make deductions and talk about possibility in medical and everyday situations.",
  iconType: "stethoscope",

  sets: [
    // ───────────────────────────────────────────────────────────
    // 🏥 SET 1: MIGHT - Expressing Possibility (A2+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u3-s1",
      title: "Set 1: Might - Maybe It's True",
      icon: "🤔",
      context: `**City Hospital Emergency Room - Initial Assessment**

La Dra. Sarah trabaja en urgencias. Cuando NO está segura del diagnóstico pero es POSIBLE, usa "MIGHT" (podría ser, tal vez sea).

**City Hospital Emergency Room - Initial Assessment**

Dr. Sarah works in the ER. When she's NOT SURE about the diagnosis but it's POSSIBLE, she uses "MIGHT" (maybe, possibly).`,

      questions: [
        {
          type: "mc",
          q: "**Patient:** 'My ankle hurts.' **Dr. Sarah:** 'It ___ be sprained. Let me examine it.'",
          options: ["might", "must", "will", "can"],
          a: "might",
          successTip: "🤔 **MIGHT** = maybe, possibly! She's not sure yet!"
        },
        {
          type: "mc",
          q: "**Dr. Sarah:** 'You have a fever and sore throat. You ___ have the flu.'",
          options: ["might", "must", "will", "should"],
          a: "might",
          successTip: "🌡️ Perfect! **MIGHT have** = it's possible, not certain!"
        },
        {
          type: "input",
          q: "**Nurse:** 'This patient has a bad cough. It ___ be bronchitis.'",
          alts: ["might", "could"],
          a: "might",
          successTip: "💡 Great! **MIGHT be** = expressing possibility!"
        },
        {
          type: "mc",
          q: "**Dr. Sarah:** 'Your headache ___ be from stress. Have you been working a lot?'",
          options: ["might", "must", "will", "can"],
          a: "might",
          successTip: "🧠 Excellent! **MIGHT be from** = possible cause!"
        },
        {
          type: "mc",
          q: "**Patient shows rash.** **Dr. Sarah:** 'This ___ be an allergic reaction.'",
          options: ["might", "must", "will", "should"],
          a: "might",
          successTip: "🔴 Perfect! **MIGHT be** = maybe, she's not 100% sure!"
        },
        {
          type: "mc",
          q: "**Dr. Sarah:** 'You ___ need antibiotics, but let's wait for the test results first.'",
          options: ["might", "must", "will", "can"],
          a: "might",
          successTip: "💊 Great! **MIGHT need** = possibility, waiting to confirm!"
        },
        {
          type: "input",
          q: "**Paramedic:** 'The patient ___ have a concussion. He hit his head hard.'",
          alts: ["might", "could"],
          a: "might",
          successTip: "🤕 Excellent! **MIGHT have** = possible diagnosis!"
        },
        {
          type: "mc",
          q: "**Dr. Sarah:** 'Your stomach pain ___ be food poisoning. What did you eat today?'",
          options: ["might", "must", "will", "can"],
          a: "might",
          successTip: "🤢 Perfect! **MIGHT be** = investigating possibilities!"
        },
        {
          type: "mc",
          q: "**Nurse:** 'She's very pale. She ___ be anemic.'",
          options: ["might", "must", "will", "should"],
          a: "might",
          successTip: "😰 Great! **MIGHT be** = possible medical condition!"
        },
        {
          type: "mc",
          q: "**Dr. Sarah:** 'Your symptoms ___ indicate diabetes. We should do blood work.'",
          options: ["might", "must", "will", "can"],
          a: "might",
          successTip: "🩺 Outstanding! **MIGHT indicate** = possible diagnosis!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🏥 SET 2: COULD - Another Way to Express Possibility (A2+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u3-s2",
      title: "Set 2: Could - Another Possibility",
      icon: "💭",
      context: `**Emergency Room - Considering Options**

"COULD" es otra forma de expresar posibilidad, igual que "might". Los dos significan lo mismo cuando hablamos de posibilidades actuales.

**Emergency Room - Considering Options**

"COULD" is another way to express possibility, just like "might". Both mean the same when talking about current possibilities.`,

      questions: [
        {
          type: "mc",
          q: "**Dr. Sarah:** 'Your chest pain ___ be heartburn, or it could be something more serious.'",
          options: ["could", "must", "will", "should"],
          a: "could",
          successTip: "💭 **COULD** = maybe, it's possible! Same as might!"
        },
        {
          type: "match",
          q: "**Match: Both express the SAME possibility**",
          pairs: [
            { left: "It might be broken", right: "It could be broken" },
            { left: "She might have a cold", right: "She could have a cold" },
            { left: "He might need surgery", right: "He could need surgery" },
            { left: "You might be allergic", right: "You could be allergic" }
          ],
          successTip: "✅ Perfect! MIGHT and COULD mean the same for possibilities!"
        },
        {
          type: "mc",
          q: "**Patient has swollen leg.** **Dr. Sarah:** 'It ___ be a sprain, or it could be a fracture.'",
          options: ["could", "must", "will", "can"],
          a: "could",
          successTip: "🦵 Great! **COULD be** = expressing possibility!"
        },
        {
          type: "input",
          q: "**Nurse:** 'His symptoms ___ indicate pneumonia. We should do an X-ray.'",
          alts: ["could", "might"],
          a: "could",
          successTip: "🫁 Excellent! **COULD indicate** = maybe, possibly!"
        },
        {
          type: "mc",
          q: "**Dr. Sarah:** 'The test results ___ come back today, or they might take until tomorrow.'",
          options: ["could", "must", "will", "should"],
          a: "could",
          successTip: "📋 Perfect! **COULD come back** = it's possible!"
        },
        {
          type: "mc",
          q: "**Paramedic:** 'This patient ___ be having a stroke. We need to hurry.'",
          options: ["could", "must", "will", "can"],
          a: "could",
          successTip: "🚑 Great! **COULD be having** = serious possibility!"
        },
       {
  type: "order",
  q: "**Traduce:** 'Podría ser una distensión muscular.'",
  words: ["It", "could", "be", "a", "muscle", "strain"],
  extra: ["must", "will", "can"],
  a: ["It", "could", "be", "a", "muscle", "strain"],
  successTip: "💡 Remember: **COULD + base verb** = expressing possibility!"
},
        {
          type: "input",
          q: "**Dr. Sarah:** 'Your daughter ___ have chickenpox. Those spots look suspicious.'",
          alts: ["could", "might"],
          a: "could",
          successTip: "🔴 Perfect! **COULD have** = possible diagnosis!"
        },
        {
          type: "mc",
          q: "**Nurse:** 'The patient's dizziness ___ be from low blood pressure.'",
          options: ["could", "must", "will", "should"],
          a: "could",
          successTip: "😵 Excellent! **COULD be from** = possible cause!"
        },
        {
          type: "mc",
          q: "**Dr. Sarah:** 'Your reaction ___ be an allergic response to the medication.'",
          options: ["could", "must", "will", "can"],
          a: "could",
          successTip: "💊 Outstanding! **COULD be** = investigating possibility!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🚫 SET 3: MIGHT NOT - Negative Possibility (A2+/B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u3-s3",
      title: "Set 3: Might Not - Maybe It's Not True",
      icon: "🤷",
      context: `**Emergency Room - Ruling Out Possibilities**

Cuando algo PODRÍA NO SER cierto (pero no estás seguro), usamos "MIGHT NOT". Es la forma negativa de posibilidad.

**Emergency Room - Ruling Out Possibilities**

When something MIGHT NOT BE true (but you're not sure), we use "MIGHT NOT". It's the negative form of possibility.`,

      questions: [
        {
          type: "mc",
          q: "**Dr. Sarah:** 'It's not very swollen, so it ___ be serious.'",
          options: ["might not", "can't", "won't", "mustn't"],
          a: "might not",
          successTip: "🤷 **MIGHT NOT** = maybe it's not serious, uncertain!"
        },
        {
          type: "mc",
          q: "**Nurse:** 'Her fever is mild. She ___ need hospitalization.'",
          options: ["might not", "can't", "won't", "mustn't"],
          a: "might not",
          successTip: "🌡️ Perfect! **MIGHT NOT need** = maybe not necessary!"
        },
        {
          type: "input",
          q: "**Dr. Sarah:** 'Your symptoms are mild. You ___ ___ have the flu after all.'",
          alts: ["might not"],
          a: "might not",
          successTip: "😷 Great! **MIGHT NOT have** = maybe it's not the flu!"
        },
        {
          type: "mc",
          q: "**Patient:** 'Is my arm broken?' **Dr. Sarah:** 'It ___ be. The X-ray will tell us.'",
          options: ["might not", "can't", "won't", "mustn't"],
          a: "might not",
          successTip: "🦴 Excellent! **MIGHT NOT be** = uncertain, waiting to confirm!"
        },
        {
          type: "mc",
          q: "**Dr. Sarah:** 'The rash ___ be serious. It could just be dry skin.'",
          options: ["might not", "can't", "won't", "mustn't"],
          a: "might not",
          successTip: "🔴 Perfect! **MIGHT NOT be serious** = maybe it's minor!"
        },
       {
  type: "order",
  q: "**Traduce:** 'La prueba podría no mostrar nada malo.'",
  words: ["The", "test", "might", "not", "show", "anything", "wrong"],
  extra: ["can't", "won't", "don't"],
  a: ["The", "test", "might", "not", "show", "anything", "wrong"],
  successTip: "💡 Pattern: **MIGHT NOT + base verb** = negative possibility!"
},
        {
          type: "mc",
          q: "**Nurse:** 'His injury ___ require surgery. Let's see how it heals.'",
          options: ["might not", "can't", "won't", "mustn't"],
          a: "might not",
          successTip: "🏥 Great! **MIGHT NOT require** = maybe surgery isn't needed!"
        },
        {
          type: "input",
          q: "**Dr. Sarah:** 'Your pain ___ ___ be anything serious. It could just be muscle soreness.'",
          alts: ["might not"],
          a: "might not",
          successTip: "💪 Excellent! **MIGHT NOT be serious** = reassuring possibility!"
        },
        {
          type: "mc",
          q: "**Paramedic:** 'This patient ___ be having a heart attack. His symptoms are mild.'",
          options: ["might not", "can't", "won't", "mustn't"],
          a: "might not",
          successTip: "❤️ Perfect! **MIGHT NOT be** = less likely but not certain!"
        },
        {
          type: "mc",
          q: "**Dr. Sarah:** 'The medication ___ work for everyone, so we'll monitor you closely.'",
          options: ["might not", "can't", "won't", "mustn't"],
          a: "might not",
          successTip: "💊 Outstanding! **MIGHT NOT work** = individual variation possible!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🔍 SET 4: CAN'T - Impossible / Certain Negative (B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u3-s4",
      title: "Set 4: Can't - That's Impossible!",
      icon: "❌",
      context: `**Emergency Room - Logical Deductions**

Cuando estás SEGURO que algo es IMPOSIBLE (tienes evidencia), usamos "CAN'T" o "CANNOT". Es diferente de "might not" (incierto).

**Emergency Room - Logical Deductions**

When you're CERTAIN something is IMPOSSIBLE (you have evidence), we use "CAN'T" or "CANNOT". It's different from "might not" (uncertain).`,

      questions: [
        {
          type: "mc",
          q: "**Dr. Sarah:** 'It ___ be broken because you're walking on it without pain.'",
          options: ["can't", "might not", "couldn't", "won't"],
          a: "can't",
          successTip: "❌ **CAN'T** = impossible! You have proof (he's walking)!"
        },
        {
          type: "match",
          q: "**Match: Uncertain vs Certain**",
          pairs: [
            { left: "MIGHT NOT (uncertain)", right: "It might not be serious." },
            { left: "CAN'T (certain/impossible)", right: "It can't be serious—he's smiling!" },
            { left: "MIGHT NOT (uncertain)", right: "She might not need surgery." },
            { left: "CAN'T (certain/impossible)", right: "She can't be sick—she's running!" }
          ],
          successTip: "🎯 Perfect! CAN'T = certain it's impossible, MIGHT NOT = uncertain!"
        },
        {
          type: "mc",
          q: "**Patient walks in.** **Dr. Sarah:** 'You ___ have a broken leg. You're walking normally.'",
          options: ["can't", "might not", "couldn't", "won't"],
          a: "can't",
          successTip: "🦵 Great! **CAN'T have** = impossible based on evidence!"
        },
        {
          type: "input",
          q: "**Dr. Sarah:** 'It ___ be appendicitis because your pain is on the left side, not the right.'",
          alts: ["can't", "cannot"],
          a: "can't",
          successTip: "🏥 Excellent! **CAN'T be** = logically impossible!"
        },
        {
          type: "mc",
          q: "**Nurse:** 'She ___ be dehydrated. She just drank two liters of water.'",
          options: ["can't", "might not", "couldn't", "won't"],
          a: "can't",
          successTip: "💧 Perfect! **CAN'T be** = impossible given the facts!"
        },
      {
  type: "order",
  q: "**Traduce:** 'Él no puede tener gripe, no tiene fiebre.'",
  words: ["He", "can't", "have", "the", "flu", "—", "he", "has", "no", "fever"],
  extra: ["might", "not", "couldn't"],
  a: ["He", "can't", "have", "the", "flu", "—", "he", "has", "no", "fever"],
  successTip: "💡 Use **CAN'T + base verb** for logical impossibility with evidence!"
},
        {
          type: "mc",
          q: "**Dr. Sarah:** 'This ___ be chicken pox. You've already had it once, and you can't get it twice.'",
          options: ["can't", "might not", "couldn't", "won't"],
          a: "can't",
          successTip: "🔴 Great! **CAN'T be** = medically impossible!"
        },
        {
          type: "input",
          q: "**Paramedic:** 'It ___ be a heart attack. His ECG is completely normal.'",
          alts: ["can't", "cannot"],
          a: "can't",
          successTip: "❤️ Excellent! **CAN'T be** = test results prove it's not!"
        },
        {
          type: "mc",
          q: "**Dr. Sarah:** 'You ___ be allergic to penicillin. You've taken it many times before without problems.'",
          options: ["can't", "might not", "couldn't", "won't"],
          a: "can't",
          successTip: "💊 Perfect! **CAN'T be allergic** = history proves otherwise!"
        },
        {
          type: "mc",
          q: "**Nurse:** 'She ___ be in labor yet. She's only 32 weeks pregnant.'",
          options: ["can't", "might not", "couldn't", "won't"],
          a: "can't",
          successTip: "🤰 Outstanding! **CAN'T be** = medically too early!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🔄 SET 5: MIGHT NOT vs CAN'T (Critical Difference) (B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u3-s5",
      title: "Set 5: Might Not vs Can't",
      icon: "⚖️",
      context: `**Emergency Room - Degrees of Certainty**

¡DIFERENCIA CRÍTICA!

🤷 **MIGHT NOT** = tal vez no (incierto, 50% sure)
❌ **CAN'T** = imposible (seguro, 100% sure, con evidencia)

**Emergency Room - Degrees of Certainty**

CRITICAL DIFFERENCE!

🤷 **MIGHT NOT** = maybe not (uncertain, 50% sure)
❌ **CAN'T** = impossible (certain, 100% sure, with evidence)`,

      questions: [
        {
          type: "mc",
          q: "**Dr. Sarah sees mild symptoms:** 'It ___ be serious.' (She's uncertain)",
          options: ["might not", "can't"],
          a: "might not",
          successTip: "🤷 **MIGHT NOT** = uncertain, could still be serious!"
        },
        {
          type: "mc",
          q: "**Patient is laughing and playing:** 'He ___ be in pain.' (Impossible based on evidence)",
          options: ["can't", "might not"],
          a: "can't",
          successTip: "❌ **CAN'T** = logically impossible given the evidence!"
        },
        {
          type: "match",
          q: "**Match the correct form to each situation:**",
          pairs: [
            { left: "Uncertain (maybe not)", right: "It might not be broken." },
            { left: "Certain/Impossible", right: "It can't be broken—you're using it!" },
            { left: "Uncertain (maybe not)", right: "She might not need antibiotics." },
            { left: "Certain/Impossible", right: "She can't be sick—she's at the gym!" }
          ],
          successTip: "⚖️ Perfect! You understand the critical difference!"
        },
        {
          type: "mc",
          q: "**Symptoms are unclear:** 'It ___ be the flu, but we need to test.' (Uncertain)",
          options: ["might not", "can't"],
          a: "might not",
          successTip: "🤷 **MIGHT NOT** = unsure, need more information!"
        },
        {
          type: "mc",
          q: "**Patient has no fever:** 'It ___ be the flu. You need a fever for flu.' (Certain)",
          options: ["can't", "might not"],
          a: "can't",
          successTip: "❌ **CAN'T** = medically impossible without fever!"
        },
        {
          type: "input",
          q: "**Dr. Sarah unsure:** 'The X-ray is unclear. It ___ ___ show a fracture.'",
          alts: ["might not"],
          a: "might not",
          successTip: "🤷 **MIGHT NOT** = uncertain, unclear evidence!"
        },
        {
          type: "input",
          q: "**Dr. Sarah certain:** 'You're walking normally. It ___ be a fracture.'",
          alts: ["can't", "cannot"],
          a: "can't",
          successTip: "❌ **CAN'T** = impossible, clear evidence against it!"
        },
       {
  type: "order",
  q: "**Traduce:** 'El medicamento podría no funcionar para ti.'",
  words: ["The", "medication", "might", "not", "work", "for", "you"],
  extra: ["can't", "cannot", "won't"],
  a: ["The", "medication", "might", "not", "work", "for", "you"],
  successTip: "💡 **MIGHT NOT** shows uncertainty about negative outcomes!"
},
     {
  type: "order",
  q: "**Traduce:** 'No puede ser alergia, las pruebas son negativas.'",
  words: ["It", "can't", "be", "an", "allergy", "—", "tests", "are", "negative"],
  extra: ["might", "not", "couldn't"],
  a: ["It", "can't", "be", "an", "allergy", "—", "tests", "are", "negative"],
  successTip: "💡 **CAN'T** expresses certainty when you have proof/evidence!"
},
        {
          type: "mc",
          q: "**Choose the RIGHT ONE:** Patient has mild symptoms. Dr. Sarah says: 'It ___ be pneumonia.' (Which shows she's uncertain?)",
          options: ["might not", "can't"],
          a: "might not",
          successTip: "🤷 **MIGHT NOT** = she's not sure, still investigating!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🕒 SET 6: COULDN'T - Past Impossibility (B1)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u3-s6",
      title: "Set 6: Couldn't - Past Impossibility",
      icon: "⏮️",
      context: `**Emergency Room - Reviewing Yesterday's Case**

"COULDN'T" es la forma PASADA de "can't". Se usa para decir que algo fue IMPOSIBLE en el pasado (porque tienes evidencia).

**Emergency Room - Reviewing Yesterday's Case**

"COULDN'T" is the PAST form of "can't". It's used to say something was IMPOSSIBLE in the past (because you have evidence).`,

      questions: [
        {
          type: "mc",
          q: "**Dr. Sarah:** 'His ankle was so swollen yesterday that he ___ walk at all.'",
          options: ["couldn't", "can't", "might not", "won't"],
          a: "couldn't",
          successTip: "⏮️ **COULDN'T** = was impossible in the past!"
        },
        {
          type: "mc",
          q: "**Nurse:** 'She was in so much pain last night that she ___ sleep.'",
          options: ["couldn't", "can't", "might not", "won't"],
          a: "couldn't",
          successTip: "😫 Perfect! **COULDN'T sleep** = was impossible yesterday!"
        },
        {
          type: "input",
          q: "**Dr. Sarah:** 'Yesterday he ___ move his arm because of the injury.'",
          alts: ["couldn't", "could not"],
          a: "couldn't",
          successTip: "🦾 Great! **COULDN'T move** = past impossibility!"
        },
        {
          type: "mc",
          q: "**Patient:** 'I was so dizzy this morning that I ___ stand up.'",
          options: ["couldn't", "can't", "might not", "won't"],
          a: "couldn't",
          successTip: "😵 Excellent! **COULDN'T stand** = was impossible then!"
        },
       {
  type: "order",
  q: "**Traduce:** 'Ella estaba tan enferma que no podía comer.'",
  words: ["She", "was", "so", "sick", "that", "she", "couldn't", "eat"],
  extra: ["can't", "might", "not"],
  a: ["She", "was", "so", "sick", "that", "she", "couldn't", "eat"],
  tip: "💡 **COULDN'T** is the past form of CAN'T for past impossibility!"
},
        {
          type: "mc",
          q: "**Dr. Sarah:** 'The patient ___ breathe properly before we gave him oxygen.'",
          options: ["couldn't", "can't", "might not", "won't"],
          a: "couldn't",
          successTip: "🫁 Perfect! **COULDN'T breathe** = past medical emergency!"
        },
        {
          type: "input",
          q: "**Nurse:** 'He was unconscious, so he ___ tell us what happened.'",
          alts: ["couldn't", "could not"],
          a: "couldn't",
          successTip: "😵 Great! **COULDN'T tell** = past impossibility!"
        },
        {
          type: "mc",
          q: "**Dr. Sarah:** 'Her fever was so high last night that she ___ think clearly.'",
          options: ["couldn't", "can't", "might not", "won't"],
          a: "couldn't",
          successTip: "🌡️ Excellent! **COULDN'T think** = past condition!"
        },
        {
          type: "mc",
          q: "**Patient:** 'My leg hurt so much yesterday that I ___ go to work.'",
          options: ["couldn't", "can't", "might not", "won't"],
          a: "couldn't",
          successTip: "🦵 Perfect! **COULDN'T go** = past impossibility!"
        },
        {
  type: "order",
  q: "**Traduce:** 'El analgésico era tan fuerte que no podía conducir.'",
  words: ["The", "painkiller", "was", "so", "strong", "he", "couldn't", "drive"],
  extra: ["can't", "might", "not"],
  a: ["The", "painkiller", "was", "so", "strong", "he", "couldn't", "drive"],
successTip: "💊 Outstanding! **COULDN'T drive** = past side effect!"
    }
      ]  
    }, 

    {
      id: "m9-u3-s7",
      title: "Set 7: Complex Medical Deductions",
      icon: "🩺",
      context: `**Emergency Room - Advanced Diagnostics**

Ahora combinamos TODO: might, could, might not, can't, couldn't. Los médicos usan estos modales para razonar sobre diagnósticos complejos.

**Emergency Room - Advanced Diagnostics**

Now we combine EVERYTHING: might, could, might not, can't, couldn't. Doctors use these modals to reason about complex diagnoses.`,

      questions: [
        {
          type: "mc",
          q: "**Dr. Sarah:** 'She ___ have pneumonia because her lungs sound clear, but she ___ have bronchitis.'",
          options: ["can't / might", "might / can't", "couldn't / might", "might not / can't"],
          a: "can't / might",
          successTip: "🫁 **CAN'T have** (impossible) but **MIGHT have** (possible)!"
        },
        {
          type: "mc",
          q: "**Patient vomiting:** 'It ___ be food poisoning because you ate hours ago, but it ___ be a stomach virus.'",
          options: ["might not / could", "can't / might", "couldn't / can't", "might / can't"],
          a: "might not / could",
          successTip: "🤢 **MIGHT NOT be** (uncertain) / **COULD be** (another possibility)!"
        },
        {
  type: "order",
  q: "**Traduce:** 'Podría ser grave, pero también podría ser menor.'",
  words: ["It", "might", "be", "serious,", "but", "it", "could", "also", "be", "minor"],
  extra: ["must", "can't", "will"],
  a: ["It", "might", "be", "serious,", "but", "it", "could", "also", "be", "minor"],
  successTip: "💡 Use **MIGHT** and **COULD** together to present multiple possibilities!"
},
        {
          type: "mc",
          q: "**Dr. Sarah:** 'Yesterday he ___ move his neck, but today he can, so it ___ be a serious injury.'",
          options: ["couldn't / might not", "can't / might", "couldn't / can't", "might not / could"],
          a: "couldn't / might not",
          successTip: "🦴 **COULDN'T** (past) / **MIGHT NOT** (current uncertainty)!"
        },
        {
          type: "input",
          q: "**Nurse:** 'The rash ___ be measles, but it ___ be an allergic reaction.'",
          alts: ["might", "could", "can't"],
          a: "might",
          successTip: "🔴 **MIGHT be / COULD be** = two possible diagnoses!"
        },
        {
          type: "mc",
          q: "**Dr. Sarah:** 'It ___ be appendicitis because the pain is on the wrong side, but we ___ rule out other conditions.'",
          options: ["can't / might not", "might / can't", "couldn't / might", "might not / can't"],
          a: "can't / might not",
          successTip: "🏥 **CAN'T be** (impossible) / **MIGHT NOT rule out** (careful)!"
        },
        {
  type: "order",
  q: "**Traduce:** 'Sus síntomas podrían no indicar gripe, no tiene fiebre.'",
  words: ["His", "symptoms", "might", "not", "indicate", "flu", "—", "he", "has", "no", "fever"],
  extra: ["can't", "couldn't", "will"],
  a: ["His", "symptoms", "might", "not", "indicate", "flu", "—", "he", "has", "no", "fever"],
  successTip: "💡 **MIGHT NOT** suggests doubt, even when you have some evidence!"
},
        {
          type: "mc",
          q: "**Dr. Sarah looking at X-ray:** 'It ___ be fractured—the bone is intact, but there ___ be soft tissue damage.'",
          options: ["can't / might", "might / can't", "couldn't / might", "might not / can't"],
          a: "can't / might",
          successTip: "📸 **CAN'T be fractured** (X-ray shows) / **MIGHT be damage** (possible)!"
        },
        {
          type: "input",
          q: "**Complex case:** 'She ___ have diabetes, she ___ have thyroid problems, but we ___ know until the blood work comes back.'",
          alts: ["might", "could", "can't"],
          a: "might",
          successTip: "🩺 **MIGHT/COULD have** = multiple possibilities being investigated!"
        },
        {
          type: "mc",
          q: "**Dr. Sarah:** 'He ___ walk yesterday because of the pain, but today he's better, so it ___ be healing well.'",
          options: ["couldn't / might", "can't / could", "might not / can't", "couldn't / can't"],
          a: "couldn't / might",
          successTip: "🦵 Outstanding! **COULDN'T** (past) / **MIGHT be healing** (hopeful)!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🌍 SET 8: Deductions Beyond Medicine (B1+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u3-s8",
      title: "Set 8: Everyday Deductions",
      icon: "🌍",
      context: `**Beyond the Hospital - Real Life Situations**

Los modales de deducción no son solo para medicina. Los usamos todos los días para razonar sobre situaciones cuando no estamos 100% seguros.

**Beyond the Hospital - Real Life Situations**

Deduction modals aren't just for medicine. We use them every day to reason about situations when we're not 100% certain.`,

      questions: [
        {
          type: "mc",
          q: "**It's 3pm, phone rings:** 'That ___ be Mom. She always calls at this time.'",
          options: ["might", "can't", "couldn't", "won't"],
          a: "might",
          successTip: "📞 **MIGHT be** = probably, but not 100% certain!"
        },
        {
          type: "mc",
          q: "**Knocking on door at midnight:** 'That ___ be the postman. It's too late.'",
          options: ["can't", "might not", "couldn't", "won't"],
          a: "can't",
          successTip: "🚪 **CAN'T be** = logically impossible at midnight!"
        },
        {
          type: "mc",
          q: "**Sees lights off in neighbor's house:** 'They ___ be home. All the lights are off.'",
          options: ["might not", "can't", "couldn't", "won't"],
          a: "might not",
          successTip: "💡 **MIGHT NOT be** = uncertain, could be sleeping!"
        },
        {
          type: "order",
          q: "**Arrange: Deduction about weather:**",
          words: ["It", "might", "rain", "later", "—", "the", "sky", "looks", "dark"],
          extra: ["must", "can't", "will"],
          a: ["It", "might", "rain", "later", "—", "the", "sky", "looks", "dark"],
          successTip: "🌧️ **MIGHT rain** = possibility based on observation!"
        },
        {
          type: "mc",
          q: "**Locked out:** 'I was in a hurry and I ___ grab my keys this morning.'",
          options: ["couldn't", "can't", "might not", "won't"],
          a: "couldn't",
          successTip: "🔑 **COULDN'T grab** = past situation (was too rushed)!"
        },
        {
          type: "input",
          q: "**Traffic jam:** 'There ___ be an accident ahead. Traffic is completely stopped.'",
          alts: ["might", "could"],
          a: "might",
          successTip: "🚗 **MIGHT be** = logical deduction from evidence!"
        },
        {
          type: "mc",
          q: "**Stranger claims to know you:** 'You ___ know me. I've never seen you before in my life.'",
          options: ["can't", "might not", "couldn't", "won't"],
          a: "can't",
          successTip: "❌ **CAN'T know me** = certain it's impossible!"
        },
        {
          type: "mc",
          q: "**Restaurant closed:** 'They ___ be open. The sign says closed on Mondays.'",
          options: ["can't", "might not", "couldn't", "won't"],
          a: "can't",
          successTip: "🍽️ **CAN'T be open** = evidence (sign) makes it certain!"
        },
        {
          type: "order",
          q: "**Arrange: Past deduction about a party:**",
          words: ["The", "party", "was", "so", "loud", "I", "couldn't", "sleep"],
          extra: ["can't", "might", "not"],
          a: ["The", "party", "was", "so", "loud", "I", "couldn't", "sleep"],
          successTip: "🎉 **COULDN'T sleep** = past impossibility due to noise!"
        },
        {
          type: "mc",
          q: "**Exam results:** 'She ___ fail. She studied so hard and knew all the answers.'",
          options: ["can't", "might not", "couldn't", "won't"],
          a: "can't",
          successTip: "📚 **CAN'T fail** = certain based on her preparation!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🎯 SET 9: Advanced Mixed Contexts (B2)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u3-s9",
      title: "Set 9: Advanced Mixed Practice",
      icon: "🎯",
      context: `**Advanced Reasoning - Multiple Deductions**

Situaciones complejas donde necesitas elegir entre MIGHT, COULD, MIGHT NOT, CAN'T y COULDN'T basándote en el nivel de certeza y el tiempo verbal.

**Advanced Reasoning - Multiple Deductions**

Complex situations where you need to choose between MIGHT, COULD, MIGHT NOT, CAN'T and COULDN'T based on certainty level and tense.`,

      questions: [
        {
          type: "mc",
          q: "**Detective at crime scene:** 'The burglar ___ have entered through the window because it's locked from the inside.'",
          options: ["can't", "might not", "couldn't", "won't"],
          a: "can't",
          successTip: "🔍 **CAN'T have entered** = logically impossible (locked inside)!"
        },
        {
          type: "mc",
          q: "**Job interview:** 'She ___ get the position. She has all the qualifications, but so do other candidates.'",
          options: ["might", "can't", "couldn't", "must"],
          a: "might",
          successTip: "💼 **MIGHT get** = possible but uncertain (competition)!"
        },
        {
          type: "order",
          q: "**Arrange: Complex financial deduction:**",
          words: ["He", "couldn't", "have", "bought", "that", "car", "—", "he", "has", "no", "money"],
          extra: ["can't", "might", "not"],
          a: ["He", "couldn't", "have", "bought", "that", "car", "—", "he", "has", "no", "money"],
          successTip: "💰 **COULDN'T have bought** = past impossibility with evidence!"
        },
        {
          type: "mc",
          q: "**Scientist analyzing data:** 'The results ___ be accurate. We ran the experiment three times with identical outcomes.'",
          options: ["might not", "can't", "couldn't", "won't"],
          a: "might not",
          successTip: "🔬 **MIGHT NOT be** = cautious scientific uncertainty!"
        },
        {
          type: "input",
          q: "**Witness testimony:** 'She ___ have been at the scene. Multiple people saw her there.'",
          alts: ["might", "could"],
          a: "might",
          successTip: "👁️ **MIGHT/COULD have been** = possible, witnesses support it!"
        },
        {
          type: "mc",
          q: "**Business meeting:** 'This strategy ___ work in our market, but it's worked elsewhere, so it ___ be worth trying.'",
          options: ["might not / might", "can't / might", "couldn't / can", "might / can't"],
          a: "might not / might",
          successTip: "📊 **MIGHT NOT / MIGHT** = balancing risk and opportunity!"
        },
        {
          type: "mc",
          q: "**Historical analysis:** 'The ancient builders ___ have had modern tools, so they ___ have used simple machines.'",
          options: ["couldn't / must", "can't / might", "couldn't / might not", "might not / couldn't"],
          a: "couldn't / must",
          successTip: "🏛️ **COULDN'T have had** (impossible) / **MUST have used** (logical)!"
        },
        {
          type: "order",
          q: "**Arrange: Legal deduction with evidence:**",
          words: ["He", "can't", "be", "guilty", "—", "he", "was", "out", "of", "town"],
          extra: ["might", "not", "couldn't"],
          a: ["He", "can't", "be", "guilty", "—", "he", "was", "out", "of", "town"],
          successTip: "⚖️ **CAN'T be guilty** = alibi proves impossibility!"
        },
        {
          type: "mc",
          q: "**Weather forecast:** 'It ___ snow tomorrow based on current conditions, but the forecast ___ change overnight.'",
          options: ["might / could", "can't / might", "couldn't / can", "might not / can't"],
          a: "might / could",
          successTip: "🌨️ **MIGHT snow / COULD change** = weather uncertainty!"
        },
        {
          type: "input",
          q: "**Technical support:** 'The system ___ be down, or it ___ just be your internet connection.'",
          alts: ["might", "could"],
          a: "might",
          successTip: "💻 **MIGHT be / COULD be** = troubleshooting possibilities!"
        }
      ]
    },

    // ───────────────────────────────────────────────────────────
    // 🏆 SET 10: Mastery Level - Nuanced Deductions (B2+)
    // ───────────────────────────────────────────────────────────
    {
      id: "m9-u3-s10",
      title: "Set 10: Mastery - Nuanced Deductions",
      icon: "🏆",
      context: `**🏆 MASTERY CHALLENGE - Sophisticated Reasoning**

El nivel más alto: deducciones sofisticadas con múltiples modales, considerando evidencia, probabilidad, contexto temporal y matices lógicos.

**🏆 MASTERY CHALLENGE - Sophisticated Reasoning**

The highest level: sophisticated deductions with multiple modals, considering evidence, probability, temporal context and logical nuances.`,

      questions: [
        {
          type: "mc",
          q: "**Complex medical case:** 'The patient ___ have lupus given her symptoms, but she ___ have rheumatoid arthritis, or it ___ be neither—autoimmune conditions are difficult to diagnose.'",
          options: [
            "might / could / might",
            "can't / might / could",
            "couldn't / can / might not",
            "might not / can't / could"
          ],
          a: "might / could / might",
          successTip: "🩺 **MIGHT/COULD/MIGHT** = layered medical uncertainty!"
        },
        {
          type: "mc",
          q: "**Archaeological discovery:** 'This artifact ___ date back to Roman times based on its style, but it ___ be a medieval reproduction, so we ___ know for certain until carbon dating is complete.'",
          options: [
            "might / could / can't",
            "can't / might / couldn't",
            "couldn't / can / might not",
            "might not / can't / might"
          ],
          a: "might / could / can't",
          successTip: "🏺 **MIGHT/COULD/CAN'T** = archaeological reasoning with limits!"
        },
        {
          type: "order",
          q: "**Arrange: Sophisticated business analysis:**",
          words: ["The", "merger", "might", "succeed,", "but", "it", "could", "fail", "if", "market", "conditions", "change"],
          extra: ["must", "can't", "will"],
          a: ["The", "merger", "might", "succeed,", "but", "it", "could", "fail", "if", "market", "conditions", "change"],
          successTip: "📈 **MIGHT succeed / COULD fail** = conditional business reasoning!"
        },
        {
          type: "mc",
          q: "**Philosophical reasoning:** 'Consciousness ___ be purely biological, or it ___ involve quantum processes we don't yet understand—we simply ___ know with current scientific knowledge.'",
          options: [
            "might / might / can't",
            "can't / might / couldn't",
            "couldn't / can / might not",
            "might not / can't / might"
          ],
          a: "might / might / can't",
          successTip: "🧠 **MIGHT/MIGHT/CAN'T** = epistemic uncertainty in philosophy!"
        },
        {
          type: "input",
          q: "**Climate science:** 'The warming trend ___ be anthropogenic, it ___ be cyclical, or it ___ be a combination—the data supports multiple interpretations.'",
          alts: ["might", "could", "can't"],
          a: "might",
          successTip: "🌍 **MIGHT/COULD/MIGHT** = scientific interpretation complexity!"
        },
        {
          type: "match",
          q: "**Match: Certainty levels to appropriate modals**",
          pairs: [
            { left: "100% certain it's impossible", right: "can't / couldn't" },
            { left: "Uncertain, maybe 50%", right: "might / could / might not" },
            { left: "Logically impossible with proof", right: "can't be (with evidence)" },
            { left: "Past impossibility", right: "couldn't / could not" }
          ],
          successTip: "🎯 Mastery! You understand ALL certainty levels perfectly!"
        },
        {
          type: "mc",
          q: "**Legal precedent:** 'The court ___ have ruled differently if presented with this evidence at the time, but they ___ change their decision now, and the defendant ___ appeal based on new information.'",
          options: [
            "might / can't / might",
            "couldn't / might / can",
            "can't / couldn't / might not",
            "might not / can / couldn't"
          ],
          a: "might / can't / might",
          successTip: "⚖️ **MIGHT/CAN'T/MIGHT** = legal hypotheticals with constraints!"
        },
        {
          type: "order",
          q: "**Arrange: Nuanced historical analysis:**",
          words: ["The", "civilization", "couldn't", "have", "disappeared", "overnight", "—", "it", "might", "have", "declined", "gradually"],
          extra: ["can't", "must", "will"],
          a: ["The", "civilization", "couldn't", "have", "disappeared", "overnight", "—", "it", "might", "have", "declined", "gradually"],
          successTip: "🏛️ **COULDN'T have / MIGHT have** = historical reasoning!"
        },
        {
          type: "mc",
          q: "**Quantum physics:** 'The particle ___ be in two states simultaneously according to quantum theory, but it ___ behave classically at macro scales, which is why we ___ observe superposition in everyday life.'",
          options: [
            "might / can't / can't",
            "can't / might / couldn't",
            "couldn't / can / might not",
            "might not / can't / might"
          ],
          a: "might / can't / can't",
          successTip: "⚛️ **MIGHT/CAN'T/CAN'T** = quantum vs classical reasoning!"
        },
        {
          type: "mc",
          q: "**🏆 FINAL MASTERY:** 'Based on symptom timeline, patient history, and test results, the diagnosis ___ be viral (unlikely given antibodies), ___ be bacterial (possible but atypical presentation), or ___ be autoimmune (most likely given all factors), though we ___ rule anything out without a biopsy.'",
          options: [
            "might not / could / might / can't",
            "can't / might / could / might not",
            "couldn't / can / might / can't",
            "might / can't / could / couldn't"
          ],
          a: "might not / could / might / can't",
          successTip: "🎉 **INCREDIBLE!** You've mastered nuanced deductions with MIGHT NOT/COULD/MIGHT/CAN'T! This is B2+ level reasoning! **UNIT 3 COMPLETE!** 🏆✨"
        }
      ]
    }
  ],

  readingSet: {
    id: "m9-u3-reading",
    title: "Reading Practice: Medical Deductions",
    category: "reading",
    
    passages: [
      {
        context: `**Emergency Room - Making Initial Diagnosis**

Dr. Sarah está evaluando a un paciente con dolor en el tobillo.

**Emergency Room - Making Initial Diagnosis**

Dr. Sarah is evaluating a patient with ankle pain.`,

        text: `John has a sore ankle. It **might** be broken, or it **could** be sprained. It's not very swollen, so it **might not** be serious. It **can't** be broken because John can walk on it without severe pain.`,

        questions: [
          {
            type: "mc",
            q: "What are the TWO possibilities Dr. Sarah mentions?",
            options: ["Broken or sprained", "Infected or bruised", "Fractured or torn", "Swollen or painful"],
            a: "Broken or sprained",
            successTip: "🦴 **MIGHT/COULD** = two possible diagnoses!"
          },
          {
            type: "mc",
            q: "Why does Dr. Sarah think it **might not** be serious?",
            options: ["It's not very swollen", "John is smiling", "It's very painful", "No information"],
            a: "It's not very swollen",
            successTip: "🤷 **MIGHT NOT be serious** = based on mild swelling!"
          },
          {
            type: "mc",
            q: "Why does Dr. Sarah say it **can't** be broken?",
            options: ["John can walk on it without severe pain", "It's not swollen", "It's sprained", "He's young"],
            a: "John can walk on it without severe pain",
            successTip: "❌ **CAN'T be broken** = evidence (walking) rules it out!"
          }
        ]
      },
      {
        context: `**Medical Consultation - Diagnosing Illness**

Paciente con síntomas de enfermedad respiratoria.

**Medical Consultation - Diagnosing Illness**

Patient with respiratory illness symptoms.`,

        text: `Fay has a sore throat and isn't feeling well. She **might** have a cold. Her eyes are itchy and she has a runny nose. It **could** be hay fever instead. She **can't** have the flu because she doesn't have a high temperature.`,

        questions: [
          {
            type: "mc",
            q: "What **might** Fay have?",
            options: ["A cold", "The flu", "Appendicitis", "A broken bone"],
            a: "A cold",
            successTip: "😷 **MIGHT have a cold** = one possibility!"
          },
          {
            type: "mc",
            q: "What is the alternative possibility mentioned?",
            options: ["Hay fever", "Pneumonia", "COVID", "Bronchitis"],
            a: "Hay fever",
            successTip: "🌼 **COULD be hay fever** = another possible diagnosis!"
          },
          {
            type: "mc",
            q: "Why **can't** Fay have the flu?",
            options: ["She has no high temperature", "She's not tired", "She can walk", "No reason given"],
            a: "She has no high temperature",
            successTip: "🌡️ **CAN'T have flu** = missing key symptom (fever)!"
          }
        ]
      },
      {
        context: `**Patient History - Yesterday's Emergency**

Revisando lo que pasó ayer con un paciente.

**Patient History - Yesterday's Emergency**

Reviewing what happened yesterday with a patient.`,

        text: `Last week, Marcus was so sick that he **couldn't** get out of bed. His ankle was so sore yesterday that he **couldn't** walk to the doctor. Today he's better and can walk normally.`,

        questions: [
          {
            type: "mc",
            q: "What **couldn't** Marcus do last week?",
            options: ["Get out of bed", "Walk to work", "See the doctor", "Take medicine"],
            a: "Get out of bed",
            successTip: "🛏️ **COULDN'T get out** = past impossibility!"
          },
          {
            type: "mc",
            q: "Why **couldn't** Marcus walk to the doctor yesterday?",
            options: ["His ankle was so sore", "He had no car", "He was too tired", "No reason given"],
            a: "His ankle was so sore",
            successTip: "🦵 **COULDN'T walk** = past condition prevented it!"
          }
        ]
      },
      {
        context: `**Hospital Consultation - Differential Diagnosis**

Dr. Sarah explicando múltiples posibilidades diagnósticas.

**Hospital Consultation - Differential Diagnosis**

Dr. Sarah explaining multiple diagnostic possibilities.`,

        text: `Your stomach ache **might** be food poisoning, but it **might not** be that serious. It **could** be just something you ate. It **can't** be appendicitis because the pain is on the left side, not the right.`,

        questions: [
          {
            type: "mc",
            q: "What's one possibility for the stomach ache?",
            options: ["Food poisoning", "Appendicitis", "Heart attack", "Flu"],
            a: "Food poisoning",
            successTip: "🤢 **MIGHT be** food poisoning = possible cause!"
          },
          {
            type: "mc",
            q: "What **might not** it be?",
            options: ["That serious", "Food poisoning", "Appendicitis", "An ache"],
            a: "That serious",
            successTip: "🤷 **MIGHT NOT be serious** = maybe it's minor!"
          },
          {
            type: "mc",
            q: "Why **can't** it be appendicitis?",
            options: ["Pain is on wrong side", "Patient is walking", "No fever present", "Not explained"],
            a: "Pain is on wrong side",
            successTip: "❌ **CAN'T be appendicitis** = anatomy rules it out!"
          }
        ]
      },
      {
        context: `**Pharmacy Consultation - Possible Side Effects**

Farmacéutica explicando efectos secundarios posibles.

**Pharmacy Consultation - Possible Side Effects**

Pharmacist explaining possible side effects.`,

        text: `This medication **might** cause drowsiness, so you **might not** be able to drive. It **could** also cause nausea. However, you **can't** drink alcohol with it—that's dangerous. Last week, one patient was so drowsy he **couldn't** work.`,

        questions: [
          {
            type: "mc",
            q: "What **might** this medication cause?",
            options: ["Drowsiness", "Happiness", "Hunger", "Energy"],
            a: "Drowsiness",
            successTip: "😴 **MIGHT cause** = possible side effect!"
          },
          {
            type: "mc",
            q: "What **can't** you do with this medication?",
            options: ["Drink alcohol", "Drive a car", "Eat food", "Sleep"],
            a: "Drink alcohol",
            successTip: "🚫 **CAN'T drink alcohol** = dangerous prohibition!"
          },
          {
            type: "mc",
            q: "What **couldn't** last week's patient do?",
            options: ["Work", "Sleep", "Eat", "Walk"],
            a: "Work",
            successTip: "⏮️ **COULDN'T work** = past side effect!"
          }
        ]
      },
      {
        context: `**Post-Operative Care - Recovery Assessment**

Evaluando la recuperación del paciente después de cirugía.

**Post-Operative Care - Recovery Assessment**

Evaluating patient's recovery after surgery.`,

        text: `Your wound **might** be infected—it's red and swollen. Or it **could** just be normal healing. It **can't** be healing properly if you're still in severe pain. Yesterday you **couldn't** move your arm at all, but today you can move it a little.`,

        questions: [
          {
            type: "mc",
            q: "What are the TWO possibilities for the red, swollen wound?",
            options: ["Infected or normal healing", "Broken or sprained", "Serious or minor", "Old or new"],
            a: "Infected or normal healing",
            successTip: "🩹 **MIGHT be infected / COULD be healing** = two options!"
          },
          {
            type: "mc",
            q: "When **can't** the wound be healing properly?",
            options: ["If there's still severe pain", "If it's red", "If it's swollen", "Never"],
            a: "If there's still severe pain",
            successTip: "❌ **CAN'T be healing** if severe pain = logical deduction!"
          },
          {
            type: "mc",
            q: "What's improved since yesterday?",
            options: ["Arm movement ability", "Pain level", "Swelling", "Redness"],
            a: "Arm movement ability",
            successTip: "✅ **COULDN'T move** (past) vs **CAN move** (now) = progress!"
          }
        ]
      },
      {
        context: `**Diagnostic Reasoning - Ruling Out Conditions**
Doctor razonando sobre el diagnóstico correcto.

**Diagnostic Reasoning - Ruling Out Conditions**
Doctor reasoning about the correct diagnosis.`,

        text: `Your rash **might** be an allergic reaction to the new medication. It **could** also be a skin infection. However, it **can't** be chickenpox because you've already had it once—you can't get it twice. It **might not** be serious, but we should run some tests.`,

        questions: [
          {
            type: "mc",
            q: "What are the TWO possible causes mentioned?",
            options: ["Allergy or infection", "Virus or bacteria", "Chickenpox or measles", "Heat or cold"],
            a: "Allergy or infection",
            successTip: "🔴 **MIGHT be allergy / COULD be infection** = possibilities!"
          },
          {
            type: "mc",
            q: "Why **can't** it be chickenpox?",
            options: ["Patient had it before, can't get twice", "Patient is too old", "No fever", "Not itchy enough"],
            a: "Patient had it before, can't get twice",
            successTip: "❌ **CAN'T be chickenpox** = medical fact rules it out!"
          } 
        ] 
      } 
    ] 
  },  
  
  reviewSet: {
    id: "m9-u3-review",
    title: "Unit Review: Deductions Mastery",
    category: "review",
    context: `**🏆 UNIT 3 FINAL REVIEW - Making Deductions Mastery**

Repaso final de la Unidad 3. Demuestra que dominas:

🤔 **MIGHT/COULD** = posibilidad (no estás seguro)
🤷 **MIGHT NOT** = tal vez no (incierto)
❌ **CAN'T** = imposible (seguro, con evidencia)
⏮️ **COULDN'T** = fue imposible en el pasado

**🏆 UNIT 3 FINAL REVIEW - Making Deductions Mastery**

Final review of Unit 3. Prove you master:

🤔 **MIGHT/COULD** = possibility (not sure)
🤷 **MIGHT NOT** = maybe not (uncertain)
❌ **CAN'T** = impossible (certain, with evidence)
⏮️ **COULDN'T** = was impossible in the past`,

    questions: [
      {
        type: "mc",
        q: "**Patient has sore throat:** 'You ___ have a cold. Let's check your temperature.'",
        options: ["might", "can't", "couldn't", "must"],
        a: "might",
        successTip: "🤔 **MIGHT have** = expressing possibility!"
      },
      {
        type: "mc",
        q: "**Patient is walking normally:** 'It ___ be broken. You can walk without pain.'",
        options: ["can't", "might not", "couldn't", "won't"],
        a: "can't",
        successTip: "❌ **CAN'T be broken** = evidence proves it's impossible!"
      },
      {
        type: "mc",
        q: "**Symptoms are mild:** 'It ___ be serious, but let's do tests to be sure.'",
        options: ["might not", "can't", "couldn't", "won't"],
        a: "might not",
        successTip: "🤷 **MIGHT NOT be** = uncertain, needs confirmation!"
      },
      {
        type: "mc",
        q: "**Yesterday's condition:** 'He was so dizzy yesterday that he ___ stand up.'",
        options: ["couldn't", "can't", "might not", "won't"],
        a: "couldn't",
        successTip: "⏮️ **COULDN'T stand** = past impossibility!"
      },
      {
        type: "match",
        q: "**REVIEW: Match certainty levels**",
        pairs: [
          { left: "MIGHT/COULD (uncertain)", right: "It might be flu." },
          { left: "MIGHT NOT (uncertain negative)", right: "It might not be serious." },
          { left: "CAN'T (certain impossible)", right: "It can't be broken—you're walking!" },
          { left: "COULDN'T (past impossible)", right: "He couldn't walk yesterday." }
        ],
        successTip: "🎯 Perfect! You understand all deduction levels!"
      },
      {
        type: "mc",
        q: "**Two possibilities:** 'It ___ be sprained, or it ___ be just bruised.'",
        options: ["might / could", "can't / might", "couldn't / can", "might not / can't"],
        a: "might / could",
        successTip: "🤔 **MIGHT/COULD** = both express possibility!"
      },
      {
        type: "order",
        q: "**REVIEW: Arrange logical deduction with evidence:**",
        words: ["It", "can't", "be", "flu", "—", "you", "have", "no", "fever"],
        extra: ["might", "not", "couldn't"],
        a: ["It", "can't", "be", "flu", "—", "you", "have", "no", "fever"],
        successTip: "❌ **CAN'T be flu** = missing key symptom!"
      },
      {
        type: "mc",
        q: "**Complex deduction:** 'She ___ have pneumonia (lungs clear), but she ___ have bronchitis.'",
        options: ["can't / might", "might / can't", "couldn't / can", "might not / can't"],
        a: "can't / might",
        successTip: "🫁 **CAN'T / MIGHT** = ruling out vs possible!"
      },
      {
        type: "input",
        q: "**REVIEW: Past impossibility:** 'The pain was so bad he ___ sleep last night.'",
        alts: ["couldn't", "could not"],
        a: "couldn't",
        successTip: "⏮️ **COULDN'T sleep** = past condition prevented it!"
      },
      {
        type: "mc",
        q: "**Everyday deduction:** 'That ___ be Mom at the door. She's on vacation in Spain.'",
        options: ["can't", "might not", "couldn't", "won't"],
        a: "can't",
        successTip: "❌ **CAN'T be Mom** = logically impossible!"
      },
      {
        type: "order",
        q: "**REVIEW: Complex medical reasoning:**",
        words: ["It", "might", "not", "be", "serious,", "but", "we", "should", "test"],
        extra: ["can't", "couldn't", "must"],
        a: ["It", "might", "not", "be", "serious,", "but", "we", "should", "test"],
        successTip: "🤷 **MIGHT NOT be serious** = cautious uncertainty!"
      },
      {
        type: "mc",
        q: "**FINAL MASTERY:** 'Given the symptoms (fever, cough, fatigue) and test results (negative for flu, positive antibodies), it ___ be a bacterial infection (possible), it ___ be viral (ruled out by tests), or it ___ be something else we haven't considered yet (uncertain).'",
        options: [
          "might / can't / could",
          "can't / might / couldn't",
          "couldn't / can / might not",
          "might not / can't / might"],
        a: "might / can't / could",
        successTip: "🎉 **INCREDIBLE!** You've mastered MIGHT (possible) / CAN'T (impossible) / COULD (another possibility)! **CONGRATULATIONS - UNIT 3 MAKING DEDUCTIONS COMPLETE!** 🏆✨" 
      } 
    ] 
  } 
};